import React from "react";

import { Typography, Container, Box } from "@mui/material";
import { Text, MoreButton, getColor, Search } from "./../";
import { styled, useTheme } from "@mui/material/styles";

const ColorFilter = styled(Box)(({ theme, filterType, backgroundColor }) => {
	const bg_color_hex = getColor(backgroundColor, theme);
	let style = {
		position: "absolute",
		display: "block",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
	};

	let not_darken_filter = {
		backgroundColor: bg_color_hex ? bg_color_hex.main : "transparent",
	};
	let filtering = {};
	switch (filterType) {
		case "1":
			filtering = {
				zIndex: 1,
				mixBlendMode: "color",
				opacity: "1",
				...not_darken_filter,
			};
			break;
		case "2":
			filtering = {
				zIndex: 1,
				mixBlendMode: "multiply",
				opacity: "0.7",
				...not_darken_filter,
			};
			break;
		case "3":
			filtering = {
				zIndex: 1,
				mixBlendMode: "darken",
				opacity: "0.6",
				...not_darken_filter,
			};
			break;
		case "darken":
			filtering = {
				zIndex: 1,
				background: `linear-gradient(45deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)`,
				opacity: 1,
			};
			break;
	}
	return { ...style, ...filtering };
});

function CallToAction({
	id,
	title,
	logo,
	subtitle,
	backgroundColor,
	backgroundImage,
	headingType = "h2",
	headingVariant = "h2",
	body,
	to,
	toLabel,
	rounded,
	search,
	searchPlaceholder,
	searchVariant,
	fullWidth,
	layout,
}) {
	const classes = {};
	const theme = useTheme();
	let bg_image = null;
	const logo_url = logo && logo.length > 0 ? logo : null;
	if (backgroundImage) {
		bg_image = backgroundImage;
	}
	let bg_color = getColor(backgroundColor, theme);

	let bg_img_style = {
		backgroundImage: `url(${bg_image})`,
		backgroundSize: "cover",
		backgroundPosition: "center center",
	};

	let paddings;

	switch (layout) {
		case "bottom":
			paddings = {
				paddingTop: theme.spacing(6),
				paddingBottom: theme.spacing(6),
				[theme.breakpoints.down("sm")]: {
					paddingTop: theme.spacing(3),
					paddingBottom: theme.spacing(3),
				},
			};
			break;
		default:
			paddings = {
				paddingTop: theme.spacing(10),
				paddingBottom: theme.spacing(10),
				[theme.breakpoints.down("md")]: {
					paddingTop: theme.spacing(7),
					paddingBottom: theme.spacing(7),
				},
				[theme.breakpoints.down("sm")]: {
					paddingTop: theme.spacing(5),
					paddingBottom: theme.spacing(5),
				},
			};
			break;
	}

	let columnSizes;
	switch (layout) {
		case "bottom":
			columnSizes = { maxWidth: 960 };
			break;
		default:
			columnSizes = {
				maxWidth: 560,
				[theme.breakpoints.up("lg")]: {
					maxWidth: 640,
				},
				[theme.breakpoints.up("xl")]: {
					maxWidth: 720,
				},
				[theme.breakpoints.up("xxl")]: {
					maxWidth: 880,
				},
			};
	}

	const inner_content = (
		<Box
			key={id}
			className="cta-wrap"
			sx={{
				...(layout === "bottom" && {
					...bg_img_style,
					paddingTop: theme.spacing(27),
					[theme.breakpoints.down("md")]: {
						paddingTop: theme.spacing(24),
					},
				}),
			}}
		>
			<Box
				className={`content-row call-to-action fgcolor-light align-center ${
					fullWidth ? "full-width" : ""
				} ${rounded ? "rounded" : ""} ${
					!backgroundColor ? "transparent" : ""
				} layout-${layout ? layout : "default"}`}
				sx={{
					position: "relative",
					overflow: "hidden",
					textAlign: "center",
					...paddings,
					...(!layout && {
						...bg_img_style,
						backgroundBlendMode: "luminosity",
						backgroundColor: bg_color ? bg_color.main : "transparent",
					}),
					"&.fgcolor-light": {
						color: "white",
						"& a": {
							color: "white",
						},
					},
					"& + .content-row": {
						borderTop: "#e0e0e0 3px solid",
					},
					"&.rounded": {
						borderRadius: theme.spacing(1),
					},
					"& + .title-bar, & + .banner": {
						marginTop: 0,
					},
					"&:last-child": {
						marginBottom: 0,
					},
					"&.transparent": {
						"& header": {
							textShadow: `1px 1px 2px rgba(0,0,0,0.5)`,
						},
					},
					"&.full-width": {
						"& + .title-bar": {
							marginTop: theme.spacing(-4),
						},
					},
				}}
			>
				<Container
					className="align-center"
					sx={{
						"&:not(.align-center) > *": {
							textAlign: "left",
						},
					}}
				>
					<Box
						className="cta-column"
						sx={{
							"& > div": {
								position: "relative",
								zIndex: 2,
								paddingLeft: theme.spacing(2),
								paddingRight: theme.spacing(2),
								marginLeft: "auto",
								marginRight: "auto",
								...columnSizes,
							},
						}}
					>
						<Box>
							<header>
								{title && (
									<Typography
										variant={headingType === "h1" ? "h1" : headingVariant}
										component={headingType}
										sx={{ marginBottom: theme.spacing(2) }}
									>
										{logo_url === null ? (
											title
										) : (
											<img
												src={logo_url}
												alt={title}
												style={{
													height: "auto",
													width: "70%",
													marginBottom: theme.spacing(2),
													maxWidth: 200,
													[theme.breakpoints.up("md")]: {
														maxWidth: 250,
													},
													[theme.breakpoints.up("lg")]: {
														maxWidth: 330,
													},
												}}
											/>
										)}
									</Typography>
								)}

								{subtitle && (
									<Typography
										variant="overline"
										component="strong"
										sx={{
											marginTop: theme.spacing(2),
											marginBottom: theme.spacing(1),
											display: "block",
											fontSize: "1.2em",
											lineHeight: 1.5,
										}}
									>
										{subtitle}
									</Typography>
								)}
							</header>
							{body && <Text html={body} lineHeight="sm" />}
							{to && (
								<MoreButton
									to={to}
									toLabel={toLabel}
									sx={{
										fontSize: "1.2em",
										marginTop: theme.spacing(1),
										color: "white",
										textTransform: "none",
										textDecoration: "underline",
									}}
								/>
							)}
							{typeof search !== "undefined" && search && (
								<Search
									placeholder={searchPlaceholder}
									variant={searchVariant}
								/>
							)}
						</Box>
					</Box>
				</Container>
				{backgroundColor ? (
					<>
						<ColorFilter
							className="filter-1"
							filterType="1"
							backgroundColor={backgroundColor}
						/>
						<ColorFilter
							className="filter-2"
							filterType="2"
							backgroundColor={backgroundColor}
						/>
						<ColorFilter
							className="filter-3"
							filterType="3"
							backgroundColor={backgroundColor}
						/>
					</>
				) : (
					<ColorFilter filterType="darken" />
				)}
			</Box>
		</Box>
	);

	return headingType === "h1" ? (
		<>{inner_content}</>
	) : (
		<aside className={classes.aside}>{inner_content}</aside>
	);
}

export default CallToAction;
